import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { HouseholdRetirementService, type TimelineItem, type TimelineResponse } from '../../services/household-retirement-service';
import { splitTimeline, timelineDate, timelineIcon } from '../timeline/timelineIcons';
import { NavigateFn, SectionCard } from './shared';

const money = (v: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(v);

const Row: React.FC<{ it: TimelineItem }> = ({ it }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4, opacity: it.past ? 0.65 : 1 }}>
    <Typography variant="body2" className="fw-num" sx={{ width: 72, flexShrink: 0, color: 'text.secondary' }}>{timelineDate(it.date)}</Typography>
    <Box sx={{ display: 'flex', color: it.source === 'user' ? 'secondary.main' : 'primary.main' }}>{timelineIcon(it.icon)}</Box>
    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{it.label}</Typography>
    {it.amount != null && (
      <Typography variant="body2" className="fw-num" color={it.amount < 0 ? 'error.main' : 'success.main'}>
        {it.amount > 0 ? '+' : ''}{money(it.amount)}
      </Typography>
    )}
  </Box>
);

/**
 * Dashboard "Timeline" card (issue #39): the next five events and the last
 * three, from GET /timeline. Hidden until the timeline has something to show.
 */
export const TimelineCard: React.FC<{ onNavigate: NavigateFn; data?: TimelineResponse | null }> = ({ onNavigate, data }) => {
  const [fetched, setFetched] = useState<TimelineResponse | null>(null);
  const tl = data !== undefined ? data : fetched;
  useEffect(() => {
    if (data !== undefined) return;
    let live = true;
    HouseholdRetirementService.timeline().then((r) => { if (live) setFetched(r); }).catch(() => undefined);
    return () => { live = false; };
  }, [data]);
  if (!tl || tl.items.length === 0) return null;
  const { upcoming, past } = splitTimeline(tl.items, 5, 3);
  return (
    <SectionCard title="Timeline" subtitle="What's next, and what just happened"
      action={{ label: 'Full timeline', link: { to: '/timeline' } }} onNavigate={onNavigate}>
      <Box aria-label="Upcoming events">
        {upcoming.map((it) => <Row key={it.id} it={it} />)}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.75 }}>
        <Box sx={{ flex: 1, borderTop: 1, borderColor: 'primary.main' }} />
        <Chip size="small" color="primary" variant="outlined" label={`Now · ${timelineDate(tl.as_of, true)}`} />
        <Box sx={{ flex: 1, borderTop: 1, borderColor: 'primary.main' }} />
      </Box>
      <Box aria-label="Recent events">
        {past.map((it) => <Row key={it.id} it={it} />)}
      </Box>
    </SectionCard>
  );
};
